import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Users } from 'lucide-react';
import { Card } from '@/components/Card';
import { StatusBadge } from '@/components/StatusBadge';
import { useApi } from '@/hooks/useApi';
import { put } from '@/lib/api';

interface Job {
  id: string;
  title: string;
  department_name?: string;
  description: string;
  requirements: string[];
  nice_to_haves: string[];
  responsibilities: string[];
  job_type: string;
  location?: string;
  remote_eligible: boolean;
  salary_range?: { min: number; max: number; currency: string };
  status: string;
  hiring_manager_name?: string;
  application_count: number;
  tags: string[];
  published_at?: string;
  closed_at?: string;
  created_at: string;
}

const STATUS_OPTIONS = ['draft', 'open', 'paused', 'closed'];

export function JobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: job, loading, refetch } = useApi<Job>(`jobs/${id}`);
  const [saving, setSaving] = useState(false);

  async function changeStatus(status: string) {
    setSaving(true);
    try {
      await put(`jobs/${id}`, { status });
      refetch();
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-muted-foreground">Loading...</div>;
  if (!job) return <div className="text-muted-foreground">Job not found</div>;

  return (
    <div className="space-y-6 max-w-4xl">
      <button onClick={() => navigate('/jobs')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4" /> Back to Jobs
      </button>

      {/* Header */}
      <div className="glass rounded-xl p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-foreground">{job.title}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {job.department_name ?? 'No Department'}
              {job.hiring_manager_name && ` - Hiring Manager: ${job.hiring_manager_name}`}
            </p>
            <div className="flex items-center gap-3 mt-3">
              <StatusBadge status={job.status} />
              <span className="text-xs text-muted-foreground capitalize">{job.job_type.replace('_', ' ')}</span>
              {job.location && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <MapPin className="w-3 h-3" /> {job.location}
                </span>
              )}
              {job.remote_eligible && <span className="text-xs text-emerald-400">Remote OK</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`/candidates?job_id=${job.id}`)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-3 hover:bg-surface-2 text-sm text-foreground"
            >
              <Users className="w-3.5 h-3.5" /> {job.application_count} Applicants
            </button>
          </div>
        </div>
      </div>

      {/* Status Controls */}
      <Card>
        <h3 className="text-sm font-semibold text-foreground mb-3">Job Status</h3>
        <div className="flex flex-wrap gap-2">
          {STATUS_OPTIONS.map(status => (
            <button
              key={status}
              onClick={() => void changeStatus(status)}
              disabled={saving || status === job.status}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors capitalize ${
                status === job.status
                  ? 'bg-amber-500 text-foreground'
                  : 'bg-surface-3 text-muted-foreground hover:text-foreground hover:bg-surface-2'
              } disabled:opacity-50`}
            >
              {status}
            </button>
          ))}
        </div>
        <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
          {job.published_at && <span>Published: {job.published_at.split('T')[0]}</span>}
          {job.closed_at && <span>Closed: {job.closed_at.split('T')[0]}</span>}
          <span>Created: {job.created_at?.split('T')[0]}</span>
        </div>
      </Card>

      {/* Salary Range */}
      {job.salary_range && (
        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-2">Salary Range</h3>
          <p className="text-foreground">
            {job.salary_range.currency ?? '$'}{job.salary_range.min?.toLocaleString()} - {job.salary_range.currency ?? '$'}{job.salary_range.max?.toLocaleString()}
          </p>
        </Card>
      )}

      {/* Description */}
      <Card>
        <h3 className="text-sm font-semibold text-foreground mb-3">Description</h3>
        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{job.description}</p>
      </Card>

      {/* Requirements */}
      {(job.requirements ?? []).length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-3">Requirements</h3>
          <ul className="list-disc list-inside space-y-1">
            {job.requirements.map((req, i) => (
              <li key={i} className="text-sm text-muted-foreground">{req}</li>
            ))}
          </ul>
        </Card>
      )}

      {/* Responsibilities */}
      {(job.responsibilities ?? []).length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-3">Responsibilities</h3>
          <ul className="list-disc list-inside space-y-1">
            {job.responsibilities.map((resp, i) => (
              <li key={i} className="text-sm text-muted-foreground">{resp}</li>
            ))}
          </ul>
        </Card>
      )}

      {/* Nice to Haves */}
      {(job.nice_to_haves ?? []).length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-3">Nice to Have</h3>
          <ul className="list-disc list-inside space-y-1">
            {job.nice_to_haves.map((nth, i) => (
              <li key={i} className="text-sm text-muted-foreground">{nth}</li>
            ))}
          </ul>
        </Card>
      )}

      {/* Tags */}
      {(job.tags ?? []).length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-3">Tags</h3>
          <div className="flex flex-wrap gap-2">
            {job.tags.map(tag => (
              <span key={tag} className="px-2 py-1 bg-surface-3 rounded-md text-xs text-foreground">{tag}</span>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
